import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { CreditCard, Truck, CheckCircle, ShieldCheck, Tag, X, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const { cart, total, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    delivery_address: '',
    mobile: user?.phone || ''
  });
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [successOrderId, setSuccessOrderId] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState({ cod_enabled: true, online_enabled: true });
  const [fetchingSettings, setFetchingSettings] = useState(true);

  useEffect(() => {
    const fetchPaymentSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/payment`);
        setPaymentSettings(response.data);
        if (!response.data.cod_enabled && response.data.online_enabled) {
          setPaymentMethod('online');
        } else if (response.data.cod_enabled) {
          setPaymentMethod('cod');
        }
      } catch (error) {
        console.error('Failed to fetch payment settings:', error);
      } finally {
        setFetchingSettings(false);
      }
    };
    fetchPaymentSettings();
  }, []);

  const discountedTotal = appliedCoupon ? appliedCoupon.new_total : total;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          price: item.price
        })),
        name: formData.name,
        email: formData.email,
        delivery_address: formData.delivery_address,
        mobile: formData.mobile,
        coupon_code: appliedCoupon ? appliedCoupon.code : null
      };

      if (paymentMethod === 'online') {
        // Try Cashfree online payment
        try {
          const response = await axios.post(`${API}/orders`, orderData);
          const { payment_session_id, cf_environment } = response.data;

          if (payment_session_id && window.Cashfree) {
            const cashfree = window.Cashfree({ mode: cf_environment || 'sandbox' });
            cashfree.checkout({
              paymentSessionId: payment_session_id,
              returnUrl: `${process.env.REACT_APP_BACKEND_URL}/api/payments/success?order_id=${response.data.order_id}`
            });
            clearCart();
          } else {
            throw new Error('Payment gateway unavailable');
          }
        } catch (payErr) {
          console.error('Online payment failed:', payErr);
          const errorMsg = payErr.response?.data?.detail || 'Online payment is currently unavailable. Please try Cash on Delivery.';
          toast.error(errorMsg);
          setLoading(false);
          // Keep the selection so user knows what failed
          return;
        }
      } else {
        // Cash on Delivery - create order via COD endpoint
        try {
          const response = await axios.post(`${API}/orders/cod`, orderData);
          clearCart();
          setSuccessOrderId(response.data.order_id);
          setOrderSuccess(true);
          toast.success('Order placed successfully!');
        } catch (codErr) {
          // If COD endpoint doesn't exist, fall back to regular endpoint  
          // and handle the payment error gracefully
          try {
            const response = await axios.post(`${API}/orders`, orderData);
            clearCart();
            setSuccessOrderId(response.data.order_id || 'N/A');
            setOrderSuccess(true);
            toast.success('Order placed successfully!');
          } catch (fallbackErr) {
            toast.error(fallbackErr.response?.data?.detail || 'Failed to process order. Please try again.');
            setLoading(false);
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process order');
      setLoading(false);
    }
  };

  if (cart.length === 0 && !orderSuccess) {
    navigate('/cart');
    return null;
  }

  const handleApplyCoupon = async (e) => {
    e.preventDefault();
    if (!couponCode.trim()) return;

    setValidatingCoupon(true);
    try {
      const response = await axios.post(`${API}/coupons/apply`, {
        code: couponCode,
        cart_total: total
      });
      setAppliedCoupon(response.data);
      toast.success('Coupon applied successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired coupon');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  // Order Success Screen
  if (orderSuccess) {
    return (
      <div className="min-h-screen py-12 flex items-center justify-center" data-testid="order-success">
        <div className="max-w-lg mx-auto text-center px-4">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="h-14 w-14 text-green-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold uppercase mb-4">
            Order <span className="text-primary">Confirmed!</span>
          </h1>
          <p className="text-slate-600 mb-2">Your order has been placed successfully.</p>
          <p className="text-sm text-slate-500 mb-2">Order ID: <span className="font-mono font-semibold text-slate-700">{successOrderId}</span></p>
          <p className="text-sm text-slate-500 mb-8">Payment: Cash on Delivery</p>
          
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
            <p className="text-amber-800 text-sm font-medium">
              📞 Our team will contact you shortly to confirm your order and delivery details.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-primary hover:bg-primary/90 font-bold uppercase tracking-wider"
            >
              View My Orders
            </Button>
            <Button
              onClick={() => navigate('/products')}
              variant="outline"
              className="border-primary text-primary hover:bg-primary/5 font-bold uppercase tracking-wider"
            >
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight uppercase mb-8">
          <span className="text-primary">Checkout</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card className="p-6 border-slate-200 mb-6">
              <h2 className="text-2xl font-heading font-semibold uppercase mb-6">Delivery Details</h2>
              <form onSubmit={handleSubmit} className="space-y-4" id="checkout-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      required
                      className="mt-1"
                      data-testid="name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      required
                      className="mt-1"
                      data-testid="email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="delivery-address">Delivery Address *</Label>
                  <textarea
                    id="delivery-address"
                    value={formData.delivery_address}
                    onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                    placeholder="Enter complete delivery address"
                    required
                    rows={4}
                    className="w-full mt-1 px-4 py-2 border border-slate-200 rounded-md"
                    data-testid="delivery-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="mobile">Contact Mobile *</Label>
                  <Input
                    id="mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    placeholder="9876543210"
                    required
                    className="mt-1"
                    data-testid="mobile-input"
                  />
                </div>
              </form>
            </Card>

            {/* Payment Method Selection */}
            <Card className="p-6 border-slate-200">
              <h2 className="text-2xl font-heading font-semibold uppercase mb-6">Payment Method</h2>
              
              {fetchingSettings ? (
                <div className="py-8 text-center text-slate-500 animate-pulse">Loading payment options...</div>
              ) : !paymentSettings.cod_enabled && !paymentSettings.online_enabled ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800">Checkout Unavailable</p>
                    <p className="text-sm text-amber-700">We are currently not accepting orders. Please try again later.</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {paymentSettings.cod_enabled && (
                    <label
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        paymentMethod === 'cod'
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid="payment-cod"
                    >
                      <input
                        type="radio"
                        name="payment"
                        value="cod"
                        checked={paymentMethod === 'cod'}
                        onChange={() => setPaymentMethod('cod')}
                        className="accent-primary h-5 w-5"
                      />
                      <div className="p-2 bg-green-50 rounded-lg">
                        <Truck className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold text-slate-800">Cash on Delivery</span>
                        <p className="text-sm text-slate-500">Pay when you receive your order</p>
                      </div>
                      <ShieldCheck className="h-5 w-5 text-green-500" />
                    </label>
                  )}

                  {paymentSettings.online_enabled && (
                    <label
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        paymentMethod === 'online'
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid="payment-online"
                    >
                      <input
                        type="radio"
                        name="payment"
                        value="online"
                        checked={paymentMethod === 'online'}
                        onChange={() => setPaymentMethod('online')}
                        className="accent-primary h-5 w-5"
                      />
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <CreditCard className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <span className="font-semibold text-slate-800">Pay Online</span>
                        <p className="text-sm text-slate-500">Credit/Debit Card, UPI, Net Banking</p>
                      </div>
                    </label>
                  )}
                </div>
              )}

              <Button
                type="submit"
                form="checkout-form"
                disabled={loading || fetchingSettings || (!paymentSettings.cod_enabled && !paymentSettings.online_enabled)}
                className="w-full mt-6 bg-primary hover:bg-primary/90 font-bold uppercase tracking-wider py-6 h-auto"
                data-testid="place-order-button"
              >
                {loading ? 'Processing...' : paymentMethod === 'cod' ? 'Place Order (COD)' : 'Place Order & Pay'}
              </Button>
            </Card>
          </div>

          <div>
            <Card className="p-6 border-slate-200 sticky top-24">
              <h2 className="text-xl font-heading font-semibold uppercase mb-4">Order Summary</h2>
              <div className="space-y-2 mb-6">
                {cart.map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-slate-600">{item.name} x {item.quantity}</span>
                    <span>₹{(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                ))}
              </div>

              {/* Coupon Section */}
              <div className="mb-6 border-t pt-4">
                <p className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <Tag className="h-4 w-4 text-slate-500" /> Apply Coupon
                </p>
                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 p-3 rounded-md">
                    <div className="flex flex-col">
                      <span className="font-mono font-bold text-green-700">{appliedCoupon.code}</span>
                      <span className="text-xs text-green-600">Coupon applied successfully</span>
                    </div>
                    <button onClick={removeCoupon} className="p-1 hover:bg-green-100 rounded text-green-700 hover:text-green-900 transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleApplyCoupon} className="flex gap-2">
                    <Input
                      placeholder="Enter promo code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="font-mono uppercase text-sm"
                    />
                    <Button type="submit" variant="secondary" disabled={!couponCode || validatingCoupon}>
                      {validatingCoupon ? '...' : 'Apply'}
                    </Button>
                  </form>
                )}
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>₹{total.toLocaleString()}</span>
                </div>
                {appliedCoupon && (
                  <div className="flex justify-between text-green-600 font-medium">
                    <span>Discount ({appliedCoupon.code})</span>
                    <span>-₹{appliedCoupon.discount_amount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span className="text-green-600">Free</span>
                </div>
                <div className="border-t pt-2 flex justify-between text-xl font-bold">
                  <span>Total</span>
                  <span className="text-primary" data-testid="checkout-total">₹{discountedTotal.toLocaleString()}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;